import { useCallback, useEffect, useRef, useState } from 'react'
import { useRouter } from 'next/router'

type Extended<T> = T & Record<string, any>

function useUpdateQuery<T extends Record<string, any>>() {
  const router = useRouter()
  const query = useRef<Extended<T>>(router.query as unknown as Extended<T>)

  const updateQuery = useCallback((newQuery: Partial<Extended<T>>, overwrite?: boolean) => {
    const combinedQuery = overwrite ? newQuery : { ...query.current, ...newQuery }

    // Remove empty values
    Object.keys(combinedQuery).forEach(key => {
      if (!combinedQuery[key]) {
        delete combinedQuery[key]
      }
    })

    router.replace({
      pathname: router.pathname,
      query: combinedQuery,
    }, undefined, { shallow: true })
      .then(() => {
        query.current = combinedQuery as Extended<T>
      })
  }, [router, query])

  // Remove query params starting woth a specific key
  const removeQueryParams = useCallback((key: string) => {
    const newQuery = { ...query.current }

    Object.keys(newQuery).forEach(k => {
      if (k.startsWith(key)) {
        delete newQuery[k]
      }
    })

    router.replace({
      pathname: router.pathname,
      query: newQuery,
    }, undefined, { shallow: true })
      .then(() => {
        query.current = newQuery
      })
  }, [router])

  const resetQuery = useCallback(() => {
    router.replace({
      pathname: router.pathname,
      query: {},
    }, undefined, { shallow: true })
      .then(() => {
        query.current = {} as Extended<T>
      })
  }, [router, query])

  useEffect(() => {
    query.current = router.query as unknown as Extended<T>
  }, [query, router.query])

  return { query: query.current, updateQuery, resetQuery, removeQueryParams }
}

export default useUpdateQuery
